import {
  ColumnTypes,
  isSummableMeasure,
} from "@dataseat-dsp-client/lib-reporter";
import { putAfter, removeAt } from "helpers/arrays";
import { addFloats, numberSort } from "helpers/primitive";
import { isSalesRelatedMeasureCPA } from "model/AnalyticsPage/measuresAsColumns/salesMeasure/isSalesCheck";
import { Integer } from "model/modelTypes";
import {
  IGroupBySplitData,
  IGroupedData,
  IGroupedRow,
  ILabeledData,
  ILabeledRow,
  IParsedRow,
  IRawTextData,
} from "model/report/reportData";
import {
  Cohorts,
  DimensionIds,
  Events,
  MetricDictionaryType,
  TCalculateMetric,
  fromCustomEventName,
} from "model/reporterDb/dbTableCampaign";
import { GRANULARITY_TYPES } from "model/reporterDb/granularity";
import * as R from "ramda";
import { DataDisplayMode } from "../dataDisplayMode";
import {
  identifyConvertData as defaultIdentifyConvertData,
  parseData as defaultParseData,
  foldAggrColumnsMulti,
} from "./helpers";

type IMeasure = {
  id: string;
  label?: string;
  columnType?: any;
  eventId?: string;
  cohort?: Cohorts;
};

export type IConstructorArgs = {
  measures: IMeasure[];
  granularity?: GRANULARITY_TYPES;
  groupBy: string[];
  rawTextData: IRawTextData;
  metricsDict?: MetricDictionaryType<any>;
  calculateMetricTotal?: TCalculateMetric;
  view?: DataDisplayMode;
  dateRange?: Date[] | string[];
  normalizeValuesFnDict?;
  normalizeGranularity?: (
    g?: GRANULARITY_TYPES,
    value?: string
  ) => string | undefined;
  columnConvertFns?;
  customHelperFns?: any;
};

type INormalize = () => IGroupedData | IGroupBySplitData;

type IGroupRowsRec = ({
  groupDepth,
  data,
}: {
  groupDepth: Integer;
  data: ILabeledData;
}) => { rows: IGroupedData; totals: ILabeledRow };

type ICalcTotals = (data: IGroupedData) => ILabeledRow;

const sortWithinGroup = (result: IGroupedData, groupId: string) => {
  if (
    //TODO this specific detail does not belong here
    groupId === DimensionIds.APP_BUNDLE_COLUMN_ID ||
    groupId === DimensionIds.CAMPAIGN_BUNDLE_COLUMN_ID ||
    groupId === DimensionIds.CITY_COLUMN_ID
  ) {
    return result.sort((a, b) =>
      numberSort(b[Events.impression], a[Events.impression])
    );
  }

  return result.sort((a, b) => a[groupId]?.localeCompare?.(b[groupId]));
};

export const reorderRowByGranularitySplit =
  (granularitySplitIndex: Integer) => (row: IParsedRow) => {
    const [granularity, ...restRow] = row;
    return putAfter({
      arr: restRow,
      index: granularitySplitIndex,
      value: granularity,
    });
  };
export class ReportNormalizer {
  aggregatableMeasures: IMeasure[]; // these measures totals = just adding values
  calculatedCPAMeasures: IMeasure[]; // CPA measures totals are calculated from custom events totals. This is for old CPA measures
  calculatedCPAMeasuresV2: IMeasure[]; // CPA measures totals are calculated from custom events totals
  calculatedCPAForSales: IMeasure[]; // CPA totals are calculated from sales/spend or sales(cohorted)/spend totals,
  calculatedMeasures: string[]; // these measures totals are calculated from other measures totals
  granularity?: GRANULARITY_TYPES;
  groupBy: string[];
  labeledData: ILabeledData;
  calculateMetricTotal?: TCalculateMetric;
  view?: DataDisplayMode;
  dateRange?: Date[] | string[];
  normalizeFnDict?;
  normalizeGranularity?: (
    g?: GRANULARITY_TYPES,
    value?: string
  ) => string | undefined;
  columnIds: string[];

  helperFns: {
    parseData: (rawTextData: IRawTextData) => IParsedRow[];
    identifyConvertData: (
      parsedData: IParsedRow[],
      columnIds: string[],
      columnConvertFns: any
    ) => ILabeledData;
  };

  constructor({
    measures,
    granularity,
    groupBy,
    rawTextData,
    calculateMetricTotal,
    view,
    dateRange,
    normalizeValuesFnDict,
    normalizeGranularity,
    columnConvertFns,
    customHelperFns,
  }: IConstructorArgs) {
    this.view = view;
    this.dateRange = dateRange;
    this.aggregatableMeasures = [];
    this.calculatedCPAMeasures = [];
    this.calculatedCPAMeasuresV2 = [];
    this.calculatedCPAForSales = [];
    this.calculatedMeasures = [];
    this.calculateMetricTotal = calculateMetricTotal;
    this.normalizeFnDict = normalizeValuesFnDict;
    this.normalizeGranularity = normalizeGranularity;
    this.helperFns = {
      parseData: customHelperFns?.parseData ?? defaultParseData,
      identifyConvertData:
        customHelperFns?.identifyConvertData ?? defaultIdentifyConvertData,
    };

    measures.forEach((m) => {
      if (m.columnType === "Dimension") return;

      if (isSummableMeasure(m)) {
        this.aggregatableMeasures.push(m);
        return;
      }

      //not summable: pick how its total gets recalculated
      if (
        m.columnType === ColumnTypes.AppMeasureCPA ||
        m.columnType === ColumnTypes.CohortedAppMeasureCPA ||
        //SKAN CV CPA/pCPA have no count column to pair with, they end up as "-"
        m.columnType === ColumnTypes.DimensionAsMeasureCPA ||
        m.columnType === ColumnTypes.DimensionAsMeasurePCPA
      ) {
        this.calculatedCPAMeasures.push(m);
      } else if (m.columnType === ColumnTypes.AppMeasureCPAV2) {
        this.calculatedCPAMeasuresV2.push(m);
      } else if (isSalesRelatedMeasureCPA(m)) {
        this.calculatedCPAForSales.push(m);
      } else {
        this.calculatedMeasures.push(m.id);
      }
    });

    const indexOfGranularity = groupBy.indexOf("GRANULARITY");
    this.granularity = granularity;
    this.groupBy =
      granularity === "ALL" ? removeAt(groupBy, indexOfGranularity) : groupBy;
    this.columnIds = measures.map((m) => m.id);

    let parsedData = this.helperFns.parseData(rawTextData);

    if (granularity !== "ALL" && indexOfGranularity > -1) {
      //on server report is always grouped by granularity first, despite desired groupBy order
      parsedData = parsedData.map(
        reorderRowByGranularitySplit(indexOfGranularity)
      );
    }
    this.labeledData = this.helperFns.identifyConvertData(
      parsedData,
      this.columnIds,
      columnConvertFns
    );
  }

  normalize: INormalize = () => {
    if (!this.groupBy.length) {
      return this.normalizeValues(this.labeledData);
    }

    if (this.groupBy.length === 1) {
      return this.normalizeValues(
        sortWithinGroup(
          foldAggrColumnsMulti(
            this.labeledData,
            this.groupBy,
            this.columnIds,
            {
              default: R.sum,
            },
            false
          ),
          this.groupBy[0]
        )
      );
    }

    const { rows } = this.groupRows({ groupDepth: 0, data: this.labeledData });
    return rows;
  };

  normalizeValues = (data: any[] = []) => {
    return data?.map((currentRow) => {
      const normalized = { ...currentRow };
      Object.keys(currentRow).forEach((key) => {
        if (this.normalizeFnDict?.[key]) {
          // NOTE: preserve the original value as _key
          normalized[`_${key}`] = currentRow[key];
          normalized[key] = this.normalizeFnDict[key](
            currentRow[key],
            currentRow
          );
        }
        if (
          key === DimensionIds.GRANULARITY_COLUMN_ID &&
          this.normalizeGranularity
        ) {
          normalized[`_${key}`] = currentRow[key];
          normalized[key] = this.normalizeGranularity(
            this.granularity,
            currentRow[key]
          );
        }
      });
      return normalized;
    });
  };

  groupRows: IGroupRowsRec = ({ groupDepth, data }) => {
    const groupId = this.groupBy[groupDepth];

    if (groupDepth === this.groupBy.length - 1) {
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_topLevelSplit, ...restOfTheColumns] = this.columnIds;
      return {
        rows: this.normalizeValues(
          sortWithinGroup(
            foldAggrColumnsMulti(
              data,
              this.groupBy,
              restOfTheColumns,
              {
                default: R.sum,
              },
              false
            ),
            groupId
          )
        ),
        totals: this.calcTotals(data),
      };
    }

    let result: IGroupedData = [];

    const dataOrderedByGroup = data.sort((a, b) =>
      a[groupId]?.localeCompare?.(b[groupId])
    );
    let currentGroupValue;
    let startIndex = 0;
    let lastIndex = 0;

    while (startIndex < dataOrderedByGroup.length) {
      currentGroupValue = data[startIndex][groupId];

      while (
        data[lastIndex] &&
        data[lastIndex][groupId] === currentGroupValue
      ) {
        lastIndex++;
      }

      const { rows, totals } = this.groupRows({
        groupDepth: groupDepth + 1,
        data: data.slice(startIndex, lastIndex).map((row: IGroupedRow) => {
          //eslint-disabled-next-line @typescript-eslint/no-unused-vars
          const { [groupId]: _, ...restRow } = row;
          return restRow;
        }),
      });

      result.push({
        [groupId]: currentGroupValue,
        rows,
        ...totals,
      });

      startIndex = lastIndex;
    }

    result = this.normalizeValues(sortWithinGroup(result, groupId));

    return {
      rows: result,
      totals: this.calcTotals(result),
    };
  };

  calcTotals: ICalcTotals = (data) => {
    const totals = data.reduce((aggr, row) => {
      this.aggregatableMeasures.forEach(({ id }) => {
        aggr[id] = addFloats([aggr[id], row[id]])?.toString();
      });
      return aggr;
    }, {});

    this.calculatedCPAForSales.forEach((m) => {
      const salesColumnId =
        m.columnType === ColumnTypes.Cohorted
          ? this.aggregatableMeasures.find(
              (c) =>
                c.eventId === Events.sales &&
                c.columnType === ColumnTypes.Cohorted &&
                c.cohort === m.cohort
            )?.id
          : this.aggregatableMeasures.find(
              (c) =>
                c.id === Events.sales ||
                (c.eventId === Events.sales &&
                  (!c.columnType || c.columnType === ColumnTypes.Event))
            )?.id;
      if (!salesColumnId) {
        totals[m.id] = "-";
        return;
      }
      totals[m.id] = this.calculateMetricTotal
        ?.cpa({ ...totals, cpaEvent: totals[salesColumnId] })
        ?.toString?.();
    });

    this.calculatedCPAMeasures.forEach((m) => {
      const hasCountKind = fromCustomEventName(m.id)?.kind === "count";
      const eventCountColumnId = this.aggregatableMeasures.find(
        (c) =>
          c.eventId === m.eventId &&
          c.columnType === ColumnTypes.AppMeasure &&
          c.cohort === m.cohort
      )?.id;
      totals[m.id] =
        hasCountKind && eventCountColumnId //CPA is calculated based on count-kind events
          ? this.calculateMetricTotal
              ?.cpa({ ...totals, cpaEvent: totals[eventCountColumnId] })
              ?.toString?.()
          : "-";
    });

    this.calculatedCPAMeasuresV2.forEach(({ id, eventId, cohort }) => {
      const eventCountColumnId = this.aggregatableMeasures.find(
        (c) =>
          c.eventId === eventId &&
          c.columnType === ColumnTypes.AppMeasureV2 &&
          c.cohort === cohort
      )?.id;
      totals[id] = eventCountColumnId
        ? this.calculateMetricTotal
            ?.cpa({ ...totals, cpaEvent: totals[eventCountColumnId] })
            ?.toString?.()
        : "-";
    });

    this.calculatedMeasures.forEach((mId) => {
      totals[mId] = this.calculateMetricTotal?.[mId]?.(totals)?.toString?.();
    });
    return totals;
  };
}
